import React from 'react';
import ReactDOM from 'react-dom';
import { useTheme } from '@/contexts/ThemeContext';
import { useScrollLock } from '@/hooks/useScrollLock';
import PricingTiers from './PricingTiers';
import AppStorePlans from './AppStorePlans';
import { isNativeApp } from '@/utils/platform';

interface UsageLimitModalProps {
  isOpen: boolean;
  onClose: () => void;
  onNavigateToTerms?: () => void;
  onNavigateToPrivacy?: () => void;
}

const UsageLimitModal: React.FC<UsageLimitModalProps> = ({
  isOpen,
  onClose,
  onNavigateToTerms,
  onNavigateToPrivacy,
}) => {
  const { theme } = useTheme();
  const isDarkMode = theme === 'dark';
  // The native app sells its subscriptions in-app since 1.2, so it shows the
  // App Store plans (Apple's prices, not /api/pricing's) where the web shows
  // its pricing tiers. Review of 1.0 (9) on 2026-09-18 asked "Your app
  // references Free plan. Are there paid plans in the app? If so, where can we
  // locate those?" — in 1.2 the answer is "right here".
  //
  // The native description of the free allowance below is deliberately left
  // as it was: "each month" is a product decision, not a defect, and is not
  // to be corrected here. See entitlements.py's TIER_SPECS note.
  const native = isNativeApp();

  useScrollLock(isOpen);

  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 z-9999 flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
      onClick={onClose}
    >
      <div
        className={`relative rounded-lg p-8 w-full max-w-3xl max-h-[90vh] overflow-y-auto ${
          isDarkMode ? 'bg-gray-800 text-white' : 'bg-white text-gray-900'
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          aria-label="Close"
          className={`absolute top-4 right-4 text-2xl leading-none cursor-pointer ${
            isDarkMode ? 'text-gray-400 hover:text-white' : 'text-gray-400 hover:text-gray-700'
          }`}
        >
          ×
        </button>
        <h2 className="text-xl font-regular mb-2">
          {native ? "You've reached your monthly limit" : "You've reached your free tier limit"}
        </h2>
        <p className={`text-sm mb-6 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
          {native
            ? 'You get 30 minutes of conversation with Aleph each month. Your limit resets at the start of next month, and audiobook listening stays unlimited.'
            : 'You get 30 minutes of conversation with Aleph each month on the Free plan. Upgrade to keep the conversation going. Audiobook listening stays unlimited on every plan.'}
        </p>
        {native ? (
          <AppStorePlans
            variant="modal"
            source="usage_limit_modal"
            onPurchased={onClose}
            onNavigateToTerms={onNavigateToTerms}
            onNavigateToPrivacy={onNavigateToPrivacy}
          />
        ) : (
          <PricingTiers variant="modal" source="usage_limit_modal" />
        )}
      </div>
    </div>,
    document.body
  );
};

export default UsageLimitModal;
